import { useTranslation } from '@/i18n';
import { dateOfInput, timeOfInput } from '@/lib/clock';
import { cx } from '@/lib/cx';
import styles from './DateTimeInput.module.css';

interface DateTimeInputProps {
  /** `2026-09-02T14:20`, or '' when this end is not set. */
  value: string;
  onChange: (value: string) => void;
  /**
   * The time a day arrives with when the time box is left alone — `00:00` for the
   * start of a range, `23:59` for the end, so one click on a day means that whole
   * day rather than the instant it began.
   */
  defaultTime: string;
  /** Names the pair; each box takes it with "date" or "time" appended. */
  label: string;
  /** Bounds for the DATE box. Dates only — see the note on the time box below. */
  minDate?: string | undefined;
  maxDate?: string | undefined;
  /** The caller's own field styling, put on both boxes so they match their screen. */
  inputClassName?: string | undefined;
  className?: string | undefined;
}

/**
 * One instant, entered as a date and a time side by side.
 *
 * **Why not `datetime-local`.** Because of how its picker closes. The popup is the
 * browser's own — it is not in the page's DOM and there is no `hidePicker()` to
 * match `showPicker()` — so nothing here can dismiss it. For a combined field it
 * has no reason to dismiss itself either: after the day it still wants a time, so
 * choosing a date left the popup sitting open and the reader had to click somewhere
 * else to be rid of it, once per end of the range. A `type="date"` picker commits
 * and closes on the click that picks the day, which is the whole point of splitting
 * them.
 *
 * The time box is the price: one more control per end. It earns its place by being
 * ignorable — a date alone means the whole day — and it is what keeps a range like
 * `14:00 → 06:00` expressible at all.
 */
export const DateTimeInput = ({
  value,
  onChange,
  defaultTime,
  label,
  minDate,
  maxDate,
  inputClassName,
  className,
}: DateTimeInputProps) => {
  const { t } = useTranslation();

  const date = dateOfInput(value);
  const time = timeOfInput(value);

  const dateLabel = `${label} — ${t('common.date')}`;
  const timeLabel = `${label} — ${t('common.time')}`;

  return (
    <span className={cx(styles.pair, className)}>
      <input
        type="date"
        className={cx(styles.date, inputClassName)}
        value={date}
        min={minDate}
        max={maxDate}
        onChange={(event) => {
          const next = event.target.value;
          // Clearing the date clears the end: a time on its own is not an instant,
          // and holding on to it would leave a filter that cannot be applied.
          onChange(next === '' ? '' : `${next}T${time === '' ? defaultTime : time}`);
        }}
        aria-label={dateLabel}
        title={dateLabel}
      />

      {/*
        Disabled until there is a day for it to be a time OF. That also settles what
        this box is bounded by: nothing. `min`/`max` belong on the dates, because a
        time limit is only meaningful on the one day where the two ends meet — put on
        the box it would forbid 06:00 on every day of a range that starts at 14:00.
        The Apply button reads the pair instead, and an inverted range cannot be
        submitted.
      */}
      <input
        type="time"
        className={cx(styles.time, inputClassName)}
        value={time}
        disabled={date === ''}
        onChange={(event) => {
          const next = event.target.value;
          onChange(`${date}T${next === '' ? defaultTime : next}`);
        }}
        aria-label={timeLabel}
        title={timeLabel}
      />
    </span>
  );
};
